import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { usePresupuesto } from "../hooks/usePresupuesto";
import { PresupuestoNode, PresupuestoHead, LEYENDA_ESTADOS } from "../components/PresupuestoNode";
import { useExcelDownload, BotonExcel } from "../../reportes/pages/BalanceComprobacion";
import fs from "../../shared/fichas.module.css";
import s from "../components/presupuesto.module.css";

// Banner inline theme-aware — mismo patrón que usa el resto del sistema
// (AdminPage, ListCatalogo) en vez del Toast de SettingsUI, que está pensado
// para el panel oscuro de Ajustes y no se adapta a tema claro/oscuro.
function Toast({ msg, type }) {
  if (!msg) return null;
  const ok = type !== "error";
  const color = ok ? "#4ade80" : "#f87171";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      background: ok ? "rgba(74,222,128,0.08)" : "rgba(239,68,68,0.08)",
      border: `1px solid ${ok ? "rgba(74,222,128,0.28)" : "rgba(239,68,68,0.28)"}`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 9, padding: "11px 16px", marginBottom: 16,
      color, fontSize: "0.84rem", fontWeight: 500,
    }}>
      <i className={`fa-solid ${ok ? "fa-circle-check" : "fa-circle-exclamation"}`} />
      <span style={{ color: "var(--text-primary)" }}>{msg}</span>
    </div>
  );
}

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio",
  "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function walkTree(nodos, fn) {
  for (const n of nodos) { fn(n); walkTree(n.subcuentas || [], fn); }
}

function calcularTotalFactory(edits) {
  return function calcularTotal(node) {
    let total = 0;
    for (let m = 1; m <= 12; m++) total += Number(edits[`${node.id}-${m}`]) || 0;
    for (const h of node.subcuentas || []) total += calcularTotal(h);
    return total;
  };
}

// Suma en vivo de un mes para un nodo: lo capturado directamente + todo su
// subárbol. Las cuentas con hijas muestran este rollup (subtotal automático)
// en vez de un input, para que al escribir en una hija el padre se actualice solo.
function calcularMesFactory(edits) {
  return function calcularMes(node, mes) {
    let total = Number(edits[`${node.id}-${mes}`]) || 0;
    for (const h of node.subcuentas || []) total += calcularMes(h, mes);
    return total;
  };
}

function filtrarArbol(nodos, texto, soloConPresupuesto, calcularTotal) {
  const t = texto.trim().toLowerCase();
  function filtrarNodo(n) {
    const hijosFiltrados = (n.subcuentas || []).map(filtrarNodo).filter(Boolean);
    const nombreMatch = !t || n.nombre.toLowerCase().includes(t) || n.codigo.toLowerCase().includes(t);
    const cumpleMonto = !soloConPresupuesto || calcularTotal(n) !== 0;
    const esteVisible = nombreMatch && cumpleMonto;
    if (esteVisible || hijosFiltrados.length > 0) {
      return { ...n, subcuentas: hijosFiltrados };
    }
    return null;
  }
  return nodos.map(filtrarNodo).filter(Boolean);
}

function fmtMoney(n) {
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ToggleSwitch({ checked, onChange, label }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 9, fontSize: "0.8rem", color: "var(--text-primary)", cursor: "pointer", userSelect: "none" }}>
      <span
        onClick={() => onChange(!checked)}
        style={{
          width: 36, height: 20, borderRadius: 20, flexShrink: 0, position: "relative",
          background: checked ? "#f97316" : "var(--hover-bg)",
          border: `1px solid ${checked ? "#f97316" : "var(--border-card)"}`,
          transition: "background 0.15s, border-color 0.15s",
        }}
      >
        <span style={{
          position: "absolute", top: 1, left: checked ? 17 : 1,
          width: 16, height: 16, borderRadius: "50%", background: "#fff",
          transition: "left 0.15s", boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
        }} />
      </span>
      {label}
    </label>
  );
}

function KpiCard({ icon, acento, label, info, sub, value, progreso }) {
  return (
    <div className={`${fs.kpi} ${fs[acento]}`} style={{ alignItems: "flex-start" }}>
      <div className={fs.kpiIcon}><i className={`fa-solid ${icon}`} /></div>
      <div className={fs.kpiBody}>
        <p className={fs.kpiLabel} style={{ color: "var(--acc-text)", textTransform: "none", letterSpacing: 0, fontSize: "0.82rem" }}>
          {label}{" "}
          <i className="fa-solid fa-circle-info" title={info} style={{ color: "var(--text-muted)", fontSize: "0.72rem", cursor: "help" }} />
        </p>
        <p className={fs.kpiValue} title={value}>{value}</p>
        <p className={fs.kpiSub}>{sub}</p>
        {progreso != null && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
            <div className={fs.progressTrack} style={{ flex: 1, marginTop: 0 }}>
              <div className={fs.progressFill} style={{ width: `${Math.min(100, Math.max(0, progreso))}%` }} />
            </div>
            <span style={{ fontSize: "0.76rem", fontWeight: 700, color: "var(--text-primary)" }}>{progreso.toFixed(0)}%</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Presupuesto() {
  const { t } = useTranslation();
  const now = new Date();
  const {
    anio, setAnio, mesCorte, setMesCorte, arbol, totales, loading, saving, guardar,
  } = usePresupuesto(now.getFullYear(), now.getMonth() + 1);

  const [edits, setEdits] = useState({});
  const [busqueda, setBusqueda] = useState("");
  const [soloConPresupuesto, setSoloConPresupuesto] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [toast, setToast] = useState({ msg: "", type: "" });

  const excel = useExcelDownload("/api/finance/presupuesto/excel/", `presupuesto_${anio}.xlsx`);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "" }), 3500);
  };

  useEffect(() => {
    const next = {};
    walkTree(arbol, n => {
      for (let m = 1; m <= 12; m++) next[`${n.id}-${m}`] = String(n.meses?.[String(m)] ?? "0");
    });
    setEdits(next);
    setExpandedIds(new Set(arbol.map(n => n.id)));
  }, [arbol]);

  const calcularTotal = useMemo(() => calcularTotalFactory(edits), [edits]);
  const calcularMes = useMemo(() => calcularMesFactory(edits), [edits]);

  const arbolFiltrado = useMemo(
    () => filtrarArbol(arbol, busqueda, soloConPresupuesto, calcularTotal),
    [arbol, busqueda, soloConPresupuesto, calcularTotal],
  );

  useEffect(() => {
    if (!busqueda && !soloConPresupuesto) return;
    const ids = [];
    walkTree(arbolFiltrado, n => ids.push(n.id));
    setExpandedIds(prev => new Set([...prev, ...ids]));
  }, [busqueda, soloConPresupuesto, arbolFiltrado]);

  const onToggle = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const onEditMonth = useCallback((cuentaId, mes, valor) => {
    setEdits(prev => ({ ...prev, [`${cuentaId}-${mes}`]: valor }));
  }, []);

  async function handleGuardar() {
    const lineas = [];
    walkTree(arbol, n => {
      for (let m = 1; m <= 12; m++) {
        const num = Number(edits[`${n.id}-${m}`]);
        const monto = Number.isFinite(num) ? String(Math.round(num * 100) / 100) : "0";
        lineas.push({ cuenta: n.id, mes: m, monto });
      }
    });
    try {
      await guardar(lineas);
      showToast("Presupuesto guardado");
    } catch (e) {
      showToast(e.message, "error");
    }
  }

  const ingresos = totales?.ingresos;
  const gastos = totales?.gastos;
  const resultado = totales?.resultado_presupuestado ?? 0;
  const pctAlcance = totales?.pct_alcance_promedio;

  const ingresosPresup = ingresos?.presupuesto_acumulado ?? ingresos?.presupuesto ?? 0;
  const gastosPresup = gastos?.presupuesto_acumulado ?? gastos?.presupuesto ?? 0;

  const anios = [];
  for (let a = now.getFullYear() + 2; a >= Math.min(anio, now.getFullYear() - 4); a--) anios.push(a);
  if (!anios.includes(anio)) anios.push(anio);

  return (
    <div className={fs.page}>
      <span style={{ color: "var(--text-muted)" }}>
        <Link to="/"><span style={{ color: "var(--text-muted)" }}>Inicio </span></Link>/ <span style={{ color: "var(--text-primary)" }}>Presupuesto</span>
      </span>
      <h2 className="mt-2 me-2 p-0 title">{t("presupuesto.titulo")}</h2>
      <p className="mb-3" style={{ color: "var(--text-muted)" }}>
        {t("presupuesto.administra")}
      </p>

      <Toast msg={toast.msg} type={toast.type} />

      {/* ── Año, mes de corte y acciones ─────────────────────────────────── */}
      <div className={s.toolbar}>
        <div className={s.toolbarIcon}><i className="fa-regular fa-calendar" /></div>
        <div className={s.field}>
          <label htmlFor="pres-anio">Año</label>
          <select id="pres-anio" className={s.select} value={anio} onChange={e => setAnio(Number(e.target.value))}>
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className={s.field}>
          <label htmlFor="pres-corte">Mes de corte (vs. Real)</label>
          <select id="pres-corte" className={s.select} style={{ minWidth: 160 }} value={mesCorte}
            onChange={e => setMesCorte(Number(e.target.value))}>
            {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div className={s.toolbarActions}>
          <BotonExcel
            onClick={() => excel.descargar(`anio=${anio}&mes_corte=${mesCorte}`)}
            loading={excel.loading}
            disabled={excel.loading || loading}
          />
          <button type="button" className={fs.btnPrimary} disabled={saving || loading} onClick={handleGuardar}>
            <i className="fa-solid fa-floppy-disk" />{saving ? "Guardando…" : "Guardar presupuesto"}
          </button>
        </div>
      </div>

      {/* ── Indicadores ──────────────────────────────────────────────────── */}
      <div className={fs.kpiGrid}>
        <KpiCard icon="fa-arrow-trend-up" acento="green" label="Total Ingresos"
          info="Suma de lo presupuestado en ingresos de enero al mes de corte."
          value={`₡${fmtMoney(ingresosPresup)}`} sub={`Presupuesto acumulado a ${MESES[mesCorte - 1]}`} />
        <KpiCard icon="fa-arrow-trend-down" acento="amber" label="Total Gastos"
          info="Suma de lo presupuestado en gastos de enero al mes de corte."
          value={`₡${fmtMoney(gastosPresup)}`} sub={`Presupuesto acumulado a ${MESES[mesCorte - 1]}`} />
        <KpiCard icon="fa-chart-pie" acento="purple" label="Resultado Presupuestado"
          info="Ingresos presupuestados menos gastos presupuestados."
          value={`₡${fmtMoney(resultado)}`} sub="Ingresos - Gastos" />
        <KpiCard icon="fa-bullseye" acento="blue" label="% de Alcance Promedio"
          info="Cuánto de lo presupuestado ya se alcanzó en lo real, en promedio, al mes de corte."
          value={pctAlcance != null ? `${(pctAlcance * 100).toFixed(1)}%` : "-"} sub="Real / Presupuesto"
          progreso={pctAlcance != null ? pctAlcance * 100 : null} />
      </div>

      {/* ── Búsqueda ─────────────────────────────────────────────────────── */}
      <div className={s.filtros}>
        <div className={s.buscar}>
          <i className="fa-solid fa-magnifying-glass" />
          <input placeholder="Buscar categoría o código…" value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        </div>
        <ToggleSwitch checked={soloConPresupuesto} onChange={setSoloConPresupuesto} label="Solo con presupuesto" />
      </div>

      {/* ── Detalle ──────────────────────────────────────────────────────── */}
      <div className={s.panel}>
        <div className={s.panelHead}>
          <h3 className={s.panelTitle}>
            <span className={s.panelIcon}><i className="fa-solid fa-table-list" /></span>
            Detalle del Presupuesto
          </h3>
          <div className={s.nota} title={"Podés capturar el monto en cualquier nivel (cuenta, subcuenta o detalle). "
            + "Evitá presupuestar el mismo monto en una cuenta padre y en sus hijas, porque se sumaría doble."}>
            <i className="fa-solid fa-circle-info" />
            El Total de una cuenta incluye el monto de sus subcuentas.
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "var(--brand)", marginRight: 8 }} />Cargando…
          </div>
        ) : arbolFiltrado.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
            <i className="fa-solid fa-magnifying-glass" style={{ fontSize: "2rem", display: "block", marginBottom: 10, opacity: 0.2 }} />
            <p style={{ margin: 0, fontSize: "0.85rem" }}>Sin resultados para este filtro.</p>
          </div>
        ) : (
          <>
            <div className={s.wrap}>
              <div className={s.table}>
                <PresupuestoHead mesCorte={mesCorte} meses={MESES} />
                {arbolFiltrado.map(n => (
                  <PresupuestoNode
                    key={n.id} node={n} depth={0}
                    expandedIds={expandedIds} onToggle={onToggle}
                    edits={edits} onEditMonth={onEditMonth}
                    calcularTotal={calcularTotal} calcularMes={calcularMes} conReal mesCorte={mesCorte}
                  />
                ))}
              </div>
            </div>
            <div className={s.leyenda}>
              <span><i className="fa-solid fa-square" style={{ color: "rgba(var(--brand-rgb), 0.45)" }} /> Mes de corte</span>
              {Object.values(LEYENDA_ESTADOS).map(e => (
                <span key={e.label}><i className={`fa-solid ${e.icon} ${e.cls}`} /> {e.label} (vs. real)</span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
